// File: frontend/src/pages/parent/ChildAttendance.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';

const ChildAttendance = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch child attendance history for selected month & year
   */
  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/parent/attendance', {
        params: { month, year }
      });

      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load attendance records.');
      }
    } catch (err) {
      console.error('Error fetching child attendance:', err);
      setError(err.response?.data?.message || err.message || 'Error loading attendance.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  /**
   * Resolve status badge color
   */
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return 'bg-emerald-500 text-white';
      case 'absent':
        return 'bg-red-500 text-white';
      case 'late':
        return 'bg-amber-400 text-slate-900 font-bold';
      case 'leave':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-slate-200 text-slate-600';
    }
  };

  const summary = data?.summary || { present: 0, absent: 0, late: 0, leave: 0, attendance_percentage: 100 };
  const records = data?.records || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/parent/dashboard" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Child Attendance History</h1>
              <p className="text-xs text-slate-500">Daily attendance log & monthly summary</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Month/Year Filter Selection */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-bold text-slate-800 text-base">Filter Attendance Period</h2>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={m}>Month {m}</option>
              ))}
            </select>

            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
        </div>

        {/* Summary Counter Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-center">
            <span className="text-xs text-slate-500 font-medium uppercase block mb-1">Present</span>
            <span className="text-2xl font-extrabold text-emerald-600">{summary.present} Days</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-center">
            <span className="text-xs text-slate-500 font-medium uppercase block mb-1">Absent</span>
            <span className="text-2xl font-extrabold text-red-600">{summary.absent} Days</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-center">
            <span className="text-xs text-slate-500 font-medium uppercase block mb-1">Late</span>
            <span className="text-2xl font-extrabold text-amber-600">{summary.late} Days</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-center">
            <span className="text-xs text-slate-500 font-medium uppercase block mb-1">Leave</span>
            <span className="text-2xl font-extrabold text-blue-600">{summary.leave} Days</span>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Daily Attendance Grid View */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-4">
            <h3 className="font-bold text-slate-900 text-base">Daily Attendance Calendar Log</h3>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Present</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500"></span> Absent</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400"></span> Late</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500"></span> Leave</span>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading attendance calendar...</div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No attendance records logged for Month {month}, {year}.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
              {records.map((r) => {
                const dateObj = new Date(r.date);
                const dayNum = dateObj.getDate();
                const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });

                return (
                  <div
                    key={r.id}
                    className="p-3 rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center space-y-1.5 hover:shadow-md transition"
                  >
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{dayName}</span>
                    <span className="text-lg font-extrabold text-slate-800">{dayNum}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${getStatusBadgeClass(r.status)}`}>
                      {r.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default ChildAttendance;
